import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

// 使用懒加载导入 AdminLayout，避免循环依赖
const AdminLayout = () => import('@/components/layout/AdminLayout.vue')

/**
 * 路由元信息类型扩展
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** 页面标题 */
    title?: string
    /** 布局类型: admin-使用管理平台布局, fullscreen-全屏独立布局 */
    layout?: 'admin' | 'fullscreen'
    /** 是否需要认证，默认为 true */
    requiresAuth?: boolean
  }
}

// 白名单路由（无需认证）
const AUTH_WHITE_LIST = ['/login']

/**
 * 管理平台路由（使用 AdminLayout 布局）
 */
const adminRoutes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'Welcome',
    component: () => import('@/views/Welcome.vue'),
    meta: {
      title: '首页',
      layout: 'admin'
    }
  },
  {
    path: 'templates',
    name: 'Templates',
    component: () => import('@/views/TemplateList.vue'),
    meta: {
      title: '模板列表',
      layout: 'admin'
    }
  },
  {
    path: 'fonts',
    name: 'Fonts',
    component: () => import('@/views/FontManager.vue'),
    meta: {
      title: '字体管理',
      layout: 'admin'
    }
  },
  {
    path: 'store-basic',
    name: 'StoreBasic',
    component: () => import('@/views/StoreTestBasic.vue'),
    meta: {
      title: 'Store 测试',
      layout: 'admin'
    }
  },
  {
    path: 'platform-accounts',
    name: 'PlatformAccounts',
    component: () => import('@/views/PlatformAccountList.vue'),
    meta: {
      title: '平台账号管理',
      layout: 'admin'
    }
  },
  {
    path: 'article-tasks',
    name: 'ArticleTasks',
    component: () => import('@/views/ArticleTaskList.vue'),
    meta: {
      title: '文章发布任务',
      layout: 'admin'
    }
  },
  {
    path: 'render-tasks',
    name: 'RenderTasks',
    component: () => import('@/views/RenderTaskList.vue'),
    meta: {
      title: '富图渲染',
      layout: 'admin'
    }
  },
  {
    path: 'ai-rich-templates',
    name: 'AiRichTemplates',
    component: () => import('@/views/AiRichTemplateList.vue'),
    meta: {
      title: 'AI富图模板',
      layout: 'admin'
    }
  },
  {
    path: 'ai-rich-tasks',
    name: 'AiRichTasks',
    component: () => import('@/views/AiRichTaskList.vue'),
    meta: {
      title: 'AI富图任务',
      layout: 'admin'
    }
  },
  {
    path: 'dify-writers',
    name: 'DifyWriters',
    component: () => import('@/views/DifyWriterList.vue'),
    meta: {
      title: 'Dify写手',
      layout: 'admin'
    }
  },
  {
    path: 'ai-writers',
    name: 'AiWriters',
    component: () => import('@/views/AiWriterList.vue'),
    meta: {
      title: 'AI写手',
      layout: 'admin'
    }
  },
  {
    path: 'writer-tasks',
    name: 'WriterTasks',
    component: () => import('@/views/AiWriterTaskList.vue'),
    meta: {
      title: '写手任务',
      layout: 'admin'
    }
  },
  {
    path: 'ai-image-tasks',
    name: 'AiImageTasks',
    component: () => import('@/views/AiImageTaskList.vue'),
    meta: {
      title: 'AI生图任务',
      layout: 'admin'
    }
  },
  {
    path: 'template-synth-tasks',
    name: 'TemplateSynthTasks',
    component: () => import('@/views/TemplateSynthTaskList.vue'),
    meta: {
      title: '模板合成',
      layout: 'admin'
    }
  },
  {
    path: 'style/themes',
    name: 'ContentThemes',
    component: () => import('@/views/style/ContentThemeList.vue'),
    meta: {
      title: '内容主题',
      layout: 'admin'
    }
  },
  {
    path: 'style/themes/:themeId/categories',
    name: 'ContentThemeCategories',
    component: () => import('@/views/style/ContentThemeCategories.vue'),
    meta: {
      title: '主题分类',
      layout: 'admin'
    }
  },
  {
    path: 'style/tags',
    name: 'StyleTags',
    component: () => import('@/views/style/StyleTagList.vue'),
    meta: {
      title: '风格标签',
      layout: 'admin'
    }
  },
  {
    path: 'automation',
    name: 'Automation',
    component: () => import('@/views/automation/AutomationLayout.vue'),
    meta: {
      title: '自动化任务',
      layout: 'admin'
    },
    redirect: '/automation/tasks',
    children: [
      {
        path: 'tasks',
        name: 'AutomationTasks',
        component: () => import('@/views/automation/TaskConfigList.vue'),
        meta: {
          title: '任务配置',
          layout: 'admin'
        }
      },
      {
        path: 'executions',
        name: 'AutomationExecutions',
        component: () => import('@/views/automation/ExecutionList.vue'),
        meta: {
          title: '执行记录',
          layout: 'admin'
        }
      },
      {
        path: 'tasks/create',
        name: 'CreateAutomationTask',
        component: () => import('@/views/automation/TaskConfigForm.vue'),
        meta: {
          title: '新增任务',
          layout: 'admin'
        }
      },
      {
        path: 'tasks/:id/edit',
        name: 'EditAutomationTask',
        component: () => import('@/views/automation/TaskConfigForm.vue'),
        meta: {
          title: '编辑任务',
          layout: 'admin'
        }
      }
    ]
  }
]

/**
 * 全屏独立路由（Designer/Font 等核心功能，不使用管理平台布局）
 */
const fullscreenRoutes: RouteRecordRaw[] = [
  {
    path: '/designer',
    name: 'Designer',
    component: () => import('@/views/TemplateDesigner.vue'),
    meta: {
      title: '模板设计器',
      layout: 'fullscreen'
    }
  },
  {
    path: '/templates/:id',
    name: 'TemplateEdit',
    component: () => import('@/views/TemplateDesigner.vue'),
    meta: {
      title: '编辑模板',
      layout: 'fullscreen'
    }
  },
  {
    path: '/preview/:id',
    name: 'TemplatePreview',
    component: () => import('@/views/TemplatePreview.vue'),
    meta: {
      title: '模板预览',
      layout: 'fullscreen'
    }
  },
  {
    path: '/font-test',
    name: 'FontTest',
    component: () => import('@/views/FontTest.vue'),
    meta: {
      title: '字体测试',
      layout: 'fullscreen'
    }
  }
]

/**
 * 公开路由（无需认证）
 */
const publicRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: {
      title: '登录',
      requiresAuth: false
    }
  }
]

/**
 * 完整路由配置
 */
const routes: RouteRecordRaw[] = [
  // 公开路由（无需认证）
  ...publicRoutes,
  // 管理平台路由（使用 AdminLayout 包裹）
  {
    path: '/',
    component: AdminLayout,
    children: adminRoutes
  },
  // 全屏独立路由（不使用布局包裹）
  ...fullscreenRoutes,
  // 404 页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '页面未找到'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Global navigation guard
router.beforeEach((to, _from, next) => {
  // Set page title
  if (to.meta?.title) {
    document.title = `${to.meta.title} - Template Designer`
  } else {
    document.title = 'Template Designer'
  }

  // 检查是否需要认证
  const requiresAuth = to.meta?.requiresAuth !== false
  const isInWhiteList = AUTH_WHITE_LIST.includes(to.path)

  // 白名单路由或明确标记不需要认证的路由，直接放行
  if (isInWhiteList || !requiresAuth) {
    next()
    return
  }

  // 检查是否已登录（通过 localStorage 中的 token 判断）
  const token = localStorage.getItem('auth_token')

  if (!token) {
    // 未登录，跳转到登录页，携带 redirect 参数
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  // 已登录，放行
  next()
})

export default router
